/**
 * Base URL des appels fetch : doit finir par `/api` (routes Express : `/api/payments/init-payment`, etc.).
 * Si REACT_APP_API_URL vaut `http://localhost:5001` sans `/api`, le navigateur appelle une URL inexistante → 404.
 * L'admin peut aussi définir un override runtime (localStorage) depuis le dashboard → section Système.
 */
const API_OVERRIDE_KEY = 'alo_api_base_url';
const DEFAULT_API_BASE = 'http://localhost:5001/api';

function normalizeApiBase(raw) {
  const base = String(raw || '').trim().replace(/\/+$/, '');
  if (!base) return '';
  if (/\/api$/i.test(base)) return base;
  try {
    const u = new URL(base);
    if (!/^https?:$/i.test(u.protocol)) return '';
    if (!u.pathname || u.pathname === '/') {
      return `${base}/api`;
    }
    return base;
  } catch {
    return '';
  }
}

function resolveApiBase() {
  return normalizeApiBase(process.env.REACT_APP_API_URL) || DEFAULT_API_BASE;
}

export function getApiBaseOverride() {
  try {
    return localStorage.getItem(API_OVERRIDE_KEY) || '';
  } catch {
    return '';
  }
}

export function getApiBase() {
  return normalizeApiBase(getApiBaseOverride()) || API_URL;
}

export function setApiBaseOverride(url) {
  const normalized = normalizeApiBase(url);
  if (!normalized) return '';
  try {
    localStorage.setItem(API_OVERRIDE_KEY, normalized);
  } catch { /* noop */ }
  return normalized;
}

export function clearApiBaseOverride() {
  try {
    localStorage.removeItem(API_OVERRIDE_KEY);
  } catch { /* noop */ }
}

export const API_URL = resolveApiBase();

// Jeton JWT stocké dans localStorage 'user' à la connexion.
export function getAuthToken() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    return JSON.parse(raw)?.token || '';
  } catch {
    return '';
  }
}

// Champs volumineux et secrets exclus du localStorage (quota + hygiène) —
// métadonnées conservées à la place.
export function storageSafeUser(user) {
  if (!user || typeof user !== 'object') return user;
  const {
    verificationDocuments,
    photo,
    cinPhoto,
    portfolioPhotos,
    portfolioProjects,
    portfolioVideos,
    password,
    resetPasswordToken,
    resetPasswordTokenExpires,
    ...rest
  } = user;
  return {
    ...rest,
    verificationDocumentsCount: Array.isArray(verificationDocuments) ? verificationDocuments.length : 0,
    hasPhoto: Boolean(photo),
    hasCinPhoto: Boolean(cinPhoto),
    portfolioPhotosCount: Array.isArray(portfolioPhotos) ? portfolioPhotos.length : 0,
    portfolioProjectsCount: Array.isArray(portfolioProjects) ? portfolioProjects.length : 0,
    portfolioVideosCount: Array.isArray(portfolioVideos) ? portfolioVideos.length : 0,
  };
}

// Persiste les données utilisateur renvoyées par l'API (GET/PUT /users) SANS perdre
// le jeton JWT : ces réponses ne contiennent pas de token — l'écrasement direct
// de localStorage.user déconnectait silencieusement la session.
export function mergeStoredUser(userData) {
  if (!userData || typeof userData !== 'object') return null;
  try {
    const raw = localStorage.getItem('user');
    const prev = raw ? JSON.parse(raw) : {};
    const merged = { ...(prev && typeof prev === 'object' ? prev : {}), ...storageSafeUser(userData) };
    if (!merged.token && prev?.token) merged.token = prev.token;
    localStorage.setItem('user', JSON.stringify(merged));
    return merged;
  } catch {
    return null;
  }
}

function authHeader() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Generic API call function
const apiCall = async (endpoint, options = {}) => {
  try {
    // Important: on fusionne les headers avant d'appeler fetch.
    // Sinon, `...options` peut écraser le `Content-Type: application/json` et le backend
    // ne parse pas correctement `req.body` (problème typique sur les routes admin).
    const { headers: customHeaders, ...restOptions } = options;
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const fullUrl = `${getApiBase()}${path}`;

    const response = await fetch(fullUrl, {
      ...restOptions,
      headers: {
        'Content-Type': 'application/json',
        ...authHeader(),
        ...(customHeaders || {}),
      },
    });

    const text = await response.text();
    let data = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        // Page HTML (404 Express), texte brut, etc.
      }
    }

    if (!response.ok) {
      const hint404 =
        response.status === 404
          ? `Route introuvable (404). URL : ${fullUrl}. Vérifiez REACT_APP_API_URL dans frontend/.env (ex. http://localhost:5001/api), redémarrez npm start, et que le backend tourne (cd backend && npm start).`
          : null;
      const errorMessage =
        data.message || data.error || hint404 || `API request failed with status ${response.status}`;
      const apiError = new Error(errorMessage);
      apiError.status = response.status;
      throw apiError;
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);

    // Check for network/connection errors
    if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
      const origin = getApiBase().replace(/\/api\/?$/i, '') || getApiBase();
      throw new Error(
        `Cannot connect to server. Please make sure the backend server is running on ${origin}. Start it by running: cd backend && npm start`,
      );
    }
    
    // If it's already an Error object, throw it as is
    if (error instanceof Error) {
      throw error;
    }
    // Otherwise, wrap it in an Error
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Le jeton Bearer (joint automatiquement par apiCall) remplace l'ancien header usurpable.
const adminHeaders = () => authHeader();

// Erreurs d'authentification : session absente (401) ou droits insuffisants (403).
export const isAuthError = (err) => err?.status === 401 || err?.status === 403;

// API methods
export const api = {
  // Health check
  healthCheck: () => apiCall('/health'),

  // Contact form
  submitContact: (contactData) => 
    apiCall('/contact', {
      method: 'POST',
      body: JSON.stringify(contactData),
    }),

  getContacts: () =>
    apiCall('/contacts', {
      headers: adminHeaders(),
    }),

  subscribeNewsletter: (email) =>
    apiCall('/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  // Authentication
  register: (userData) =>
    apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  checkEmail: async (email) => {
    try {
      const response = await fetch(`${getApiBase()}/users-by-email?${new URLSearchParams({ email }).toString()}`, {
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const data = await response.json().catch(() => ({}));
      return data;
    } catch (error) {
      console.error('checkEmail error:', error);
      return { success: false };
    }
  },

  login: (credentials) =>
    apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  forgotPassword: (email) =>
    apiCall('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token, password) =>
    apiCall('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  // Retourne le profil ou null si utilisateur introuvable
  getProfile: async (userId) => {
    try {
      const response = await fetch(`${getApiBase()}/users/${userId}`, {
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const data = await response.json().catch(() => ({}));
      if (data && data.success === false && data.code === 'USER_NOT_FOUND') return null;
      if (!response.ok) throw new Error(data.message || data.error || `Server error: ${response.status}`);
      return data;
    } catch (e) {
      throw e;
    }
  },

  // Profil par email (fallback si l'ID stocké est obsolète)
  getProfileByEmail: async (email) => {
    try {
      const response = await fetch(`${getApiBase()}/users-by-email?${new URLSearchParams({ email }).toString()}`, {
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const data = await response.json().catch(() => ({}));
      if (data && data.success === false && data.code === 'USER_NOT_FOUND') return null;
      if (!response.ok) throw new Error(data.message || data.error || `Server error: ${response.status}`);
      return data;
    } catch (e) {
      throw e;
    }
  },

  updateProfile: (userId, payload) =>
    apiCall(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  // Suppression des documents de vérification (depuis "Mon profil")
  deleteVerificationDocuments: (userId) =>
    apiCall(`/users/${userId}/verification-documents`, {
      method: 'DELETE',
    }),

  // Blog posts
  getBlogs: () => apiCall('/blogs'),

  createBlog: (blogData) =>
    apiCall('/blogs', {
      method: 'POST',
      body: JSON.stringify(blogData),
    }),

  // Services
  getServices: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/services${queryString ? `?${queryString}` : ''}`);
  },

  getService: (identifier) =>
    apiCall(`/services/${identifier}`),

  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return fetch(`${getApiBase()}/upload`, {
      method: 'POST',
      body: formData,
    }).then(res => res.json());
  },

  createService: (serviceData) =>
    apiCall('/services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
      headers: adminHeaders(),
    }),

  updateService: (serviceId, serviceData) =>
    apiCall(`/services/${serviceId}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
      headers: adminHeaders(),
    }),

  deleteService: (serviceId) =>
    apiCall(`/services/${serviceId}`, {
      method: 'DELETE',
      headers: adminHeaders(),
    }),

  initializeServices: (force = false) =>
    apiCall('/services/initialize', {
      method: 'POST',
      body: JSON.stringify({ force }),
      ...(force ? { headers: adminHeaders() } : {}),
    }),

  adminResetServicesCatalog: () =>
    apiCall('/admin/services/reset', {
      method: 'POST',
      headers: adminHeaders(),
    }),

  removeDuplicateServices: () =>
    apiCall('/services/remove-duplicates', {
      method: 'POST',
    }),

  // Requests
  submitRequest: (requestData) =>
    apiCall('/requests', {
      method: 'POST',
      body: JSON.stringify(requestData),
    }),

  getRequests: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/requests${queryString ? `?${queryString}` : ''}`);
  },

  getRequest: (id) => apiCall(`/requests/${id}`),

  confirmRequestEmail: (token) => apiCall(`/requests/confirm-email/${encodeURIComponent(token)}`),

  // Providers
  getProvidersByService: (serviceId) =>
    apiCall(`/providers/service/${serviceId}`),

  submitProviderReview: (payload) =>
    apiCall('/provider-reviews', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getProviderReviews: (providerId, extra = {}) => {
    const params = { providerId, ...extra };
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/provider-reviews?${qs}`);
  },

  getProviderReviewsBatch: (providerIds, extra = {}) => {
    const params = { providerIds: providerIds.join(','), ...extra };
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/provider-reviews?${qs}`);
  },

  adminGetProviderReviews: () =>
    apiCall('/admin/provider-reviews', {
      headers: adminHeaders(),
    }),

  // Admin
  adminGetUsers: () =>
    apiCall('/admin/users', {
      headers: adminHeaders(),
    }),

  adminCreateUser: (payload) =>
    apiCall('/admin/users', {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify(payload),
    }),

  adminSeedDefaultUsers: (recreate = false) =>
    apiCall('/admin/users/seed-defaults', {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ recreate }),
    }),

  adminUpdateUser: (userId, payload) =>
    apiCall(`/admin/users/${userId}`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify(payload),
    }),

  adminDeleteUser: (userId) =>
    apiCall(`/admin/users/${userId}`, {
      method: 'DELETE',
      headers: adminHeaders(),
    }),

  // Admin - categories inside a Service.categories array
  adminAddServiceCategory: (serviceId, category) =>
    apiCall(`/admin/services/${serviceId}/categories`, {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ category }),
    }),

  adminUpdateServiceCategory: (serviceId, from, to) =>
    apiCall(`/admin/services/${serviceId}/categories`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ from, to }),
    }),

  adminDeleteServiceCategory: (serviceId, category) =>
    apiCall(`/admin/services/${serviceId}/categories`, {
      method: 'DELETE',
      headers: adminHeaders(),
      body: JSON.stringify({ category }),
    }),

  adminUpdateProviderVerification: (userId, payload) =>
    apiCall(`/admin/providers/${userId}/verification`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify(payload),
    }),

  // ===== SAV / Service Apres-Vente =====
  // Client side
  submitSupportTicket: (payload) =>
    apiCall('/support-tickets', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getMySupportTickets: (userEmail, extra = {}) => {
    const params = { userEmail, ...extra };
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/support-tickets?${qs}`);
  },

  getSupportTicket: (id) => apiCall(`/support-tickets/${id}`),

  addSupportTicketMessage: (id, payload) =>
    apiCall(`/support-tickets/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Admin side
  adminGetSupportTickets: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/support-tickets${qs ? `?${qs}` : ''}`, {
      headers: adminHeaders(),
    });
  },

  adminUpdateSupportTicket: (id, payload) =>
    apiCall(`/support-tickets/${id}`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify(payload),
    }),

  adminDeleteSupportTicket: (id) =>
    apiCall(`/support-tickets/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(),
    }),

  adminGetSupportStats: () =>
    apiCall('/admin/support-tickets/stats', {
      headers: adminHeaders(),
    }),

  // ===== Paiement Stripe =====
  getPaymentConfig: () => apiCall('/payments/config'),

  createOnSitePayment: (payload) =>
    apiCall('/payments/on-site', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  createCardPaymentIntent: (payload) =>
    apiCall('/payments/card-intent', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  initPayment: (payload) =>
    apiCall('/payments/init-payment', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getPayment: (paymentId) => apiCall(`/payments/${paymentId}`),

  getPaymentByStripeSession: (sessionId) =>
    apiCall(`/payments/stripe-session/${encodeURIComponent(sessionId)}`),

  getMyPayments: (userEmail, extra = {}) => {
    const params = { userEmail, ...extra };
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/payments?${qs}`);
  },

  adminGetPayments: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/payments${qs ? `?${qs}` : ''}`, {
      headers: adminHeaders(),
    });
  },

  adminGetSiteSettings: () =>
    apiCall('/admin/site-settings', {
      headers: adminHeaders(),
    }),

  adminSaveSiteSettings: (payload) =>
    apiCall('/admin/site-settings', {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify(payload),
    }),

  // ===== Système (serveur API + base de données) =====
  adminGetSystemStatus: () =>
    apiCall('/admin/system', {
      headers: adminHeaders(),
    }),

  adminTestDatabase: (uri) =>
    apiCall('/admin/database/test', {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ uri }),
    }),

  adminUpdateDatabase: (uri) =>
    apiCall('/admin/database', {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ uri }),
    }),

  // ===== Configuration plateforme (Stripe, e-mail, URLs, auth) =====
  adminGetConfig: () =>
    apiCall('/admin/config', {
      headers: adminHeaders(),
    }),

  adminSaveConfig: ({ values, clearKeys }) =>
    apiCall('/admin/config', {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ values, clearKeys }),
    }),

  adminTestEmail: (to) =>
    apiCall('/admin/config/test-email', {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ to }),
    }),

  adminTestStripe: () =>
    apiCall('/admin/config/test-stripe', {
      method: 'POST',
      headers: adminHeaders(),
    }),

  // Teste un backend candidat via /health — indépendant de l'API de base courante
  testApiBase: async (url) => {
    const base = normalizeApiBase(url);
    if (!base) return { success: false, base: '', message: 'URL invalide' };
    const started = Date.now();
    try {
      const res = await fetch(`${base}/health`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { success: false, base, message: data.message || `HTTP ${res.status}` };
      }
      return { success: true, base, latencyMs: Date.now() - started, data };
    } catch (e) {
      return { success: false, base, message: e.message };
    }
  },
};

export default api;
